import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV, CONTACT } from "../data/site";
import { CtaButton } from "./Buttons";

const Logo = ({ light }) => (
  <Link to="/" aria-label="Nuova Giga Print — Home" data-testid="header-logo-link" className="shrink-0">
    <img src={light ? "/img/logo-gigaprint-white.png" : "/img/logo-gigaprint.png"} alt="Giga Print" width="572" height="180" className="h-9 w-auto sm:h-10" />
  </Link>
);

const MobileMenu = ({ open, onClose }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        key="menu"
        initial={{ clipPath: "inset(0 0 100% 0)" }}
        animate={{ clipPath: "inset(0 0 0% 0)" }}
        exit={{ clipPath: "inset(0 0 100% 0)" }}
        transition={{ duration: 0.6, ease: [0.7, 0, 0.2, 1] }}
        className="fixed inset-0 z-[60] flex flex-col bg-[color:var(--ngp-ink)] px-6 pb-10 pt-24 text-white xl:hidden"
        data-testid="mobile-menu"
        data-lenis-prevent
      >
        <nav aria-label="Menu mobile" className="flex flex-1 flex-col gap-1 overflow-y-auto">
          {NAV.map((n, i) => (
            <motion.div key={n.to} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.05, duration: 0.5 }}>
              <NavLink to={n.to} end onClick={onClose} data-testid={`mobile-nav-${n.to.replace("/", "") || "home"}`}
                className={({ isActive }) => `font-display flex items-baseline gap-4 border-b border-white/10 py-3 text-3xl sm:text-4xl ${isActive ? "text-[color:var(--ngp-teal)]" : "text-white"}`}>
                <span className="eyebrow text-[11px] text-white/60">0{i + 1}</span>{n.label}
              </NavLink>
            </motion.div>
          ))}
        </nav>
        <div className="mt-8 flex flex-col gap-4">
          <CtaButton to="/contatti" onClick={onClose} testId="mobile-menu-quote-btn">Richiedi preventivo</CtaButton>
          <a href={CONTACT.phoneMainHref} className="flex items-center gap-2 text-sm text-white/80"><Phone size={16} aria-hidden="true" />{CONTACT.phoneMain}</a>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const solid = scrolled && !open;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-[70] transition-[background-color,box-shadow,padding] duration-500 ${solid ? "bg-[rgba(243,243,240,0.88)] py-2 shadow-[0_1px_0_var(--ngp-line)] backdrop-blur-xl" : "bg-transparent py-4"}`}
      >
        <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:bg-white focus:px-4 focus:py-2 focus:text-black">Vai al contenuto</a>
        <div className="mx-auto flex max-w-[1440px] items-center justify-between gap-6 px-5 sm:px-8 lg:px-12">
          <Logo light={!solid} />
          <nav aria-label="Menu principale" className="hidden items-center gap-1 xl:flex">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end data-testid={`nav-${n.to.replace("/", "") || "home"}`}
                className={({ isActive }) => `relative rounded-full px-3 py-2 text-[13px] font-medium transition-colors duration-300 ${solid ? "text-[color:var(--ngp-ink)] hover:bg-black/5" : "text-white hover:bg-white/10"} ${isActive ? "after:absolute after:bottom-1 after:left-1/2 after:h-1 after:w-1 after:-translate-x-1/2 after:rounded-full after:bg-[color:var(--ngp-teal)]" : ""}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <CtaButton to="/contatti" testId="header-quote-btn" className="hidden sm:inline-flex !py-1.5 !pl-5">Richiedi preventivo</CtaButton>
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-label={open ? "Chiudi menu" : "Apri menu"}
              data-testid="mobile-menu-toggle"
              className={`grid h-11 w-11 place-items-center rounded-full border transition-colors xl:hidden ${solid ? "border-black/15 text-[color:var(--ngp-ink)]" : "border-white/30 text-white"}`}
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>
      <MobileMenu open={open} onClose={() => setOpen(false)} />
    </>
  );
};
